/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2020, 2021
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import React, { Component } from 'react';
import { Collapse, TextInput } from '@ba/ba-ui-carbon-toolkit';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import $ from 'jquery';
import debounce from 'lodash/debounce';

import '../stylesheets/fieldDropdown.scss';

const BASE_CLASS_NAME = 'dropdownWrapper';
const BLUR_TIME = 350;


@observer
class FieldDropdown extends Component {

	constructor(props) {
		super(props);
		this.state = {
			userTextInput: this.props.textValue
		};
		this.dropdownRef = React.createRef();
	}

	render() {
		const dropdownStyle = {
			'max-height': this.props.getDropdownMaxHeight() > 0 ? `${this.props.getDropdownMaxHeight()}px` : 'none'
		};
		const dropdownContent =
			<div
				className='ba-common-dropdown dropdown'
				ref={this.dropdownRef}
				style={dropdownStyle}
			>
				<Collapse
					isOpened={this.props.open}
					autoUnmount={true}
					/* Has to use this prop to control if we want animation when dropdown content changes, check https://github.com/nkbt/react-collapse */
					hasNestedCollapse={true}
					forceInitialAnimation={true}
					onRest= {this.scrollToDropdown}
				>
					{
						this._renderMetadataTree()
					}
				</Collapse>
			</div>;

		return (
			<div
				className={BASE_CLASS_NAME}
				onBlur={this.onBlur}
				onFocus={this.onFocus}
				onKeyDown={ this.handleEscapeDropdown }
			>
				{this._renderTextInput()}
				{dropdownContent}
			</div>
		);
	}

	scrollToDropdown = () => {
		if (this.dropdownRef.current) {
			const differenceRect = this.dropdownRef.current.getBoundingClientRect().bottom - window.innerHeight;
			if ( differenceRect > 300 ) {
				const slotEditorNode = this.dropdownRef.current.closest('.slotEditor');
				slotEditorNode.scrollTop = differenceRect;
			}
		}
	}

	_renderTextInput() {
		const DEBOUNCE_WAIT = 250; //ms
		const truncatedTextStyle = {
			width: '100%',
			height:40
		};
		return (
			<TextInput
				className='itemInput'
				noBottomBorder
				value={this.state.userTextInput}
				autoFocus
				onChange={debounce(this.onChange.bind(this), DEBOUNCE_WAIT)}
				onKeyDown={debounce(this.handleKeyDown.bind(this), DEBOUNCE_WAIT)}
				style={truncatedTextStyle}
			/>
		);
	}

	onChange = (value) => {
		this.setState({
			userTextInput: value
		});
	}

	handleEscapeDropdown = (event) => {
		if (event.key === 'Escape') {
			this.props.onBlur();
		}
	}

	handleKeyDown = (event) => {
		if (event.key === 'ArrowDown') {
			const currentDropNode = this.dropdownRef.current;
			const muiList = $(currentDropNode).find('div.ba-common-list')[0];
			if (muiList) {
				event.preventDefault();
				muiList.focus();
			}
		} else if (event.key === 'Enter' && this.state.userTextInput === '') {
			event.preventDefault();
			/* when replaceSlotItem = true, we want to modify slot item,
			 * in this case delete when nothing is selected,
			 * otherwise close the dropdown */
			if (this.props.replaceSlotItem) {
				this.props.a11Y.deleteSlotItem(this.props.slotDataItemHandler);
			} else {
				this.props.onBlur();
			}
		}
	}

	/* setting timeout to prevent dropdown from closing
	when tabbing into it onBlur is called before onFocus*/
	onBlur = () => {
		this.blurTimer = setTimeout(this.props.onBlur, BLUR_TIME);
	}

	onFocus = () => {
		if(this.blurTimer) {
			clearTimeout(this.blurTimer);
			this.blurTimer = null;
		}
	}

	performSelection(selectedValue) {
		this.props.slotDataItemHandler.insertItemFromTypeInSlot(selectedValue, this.props.sourceId, { replaceSlotItem : this.props.replaceSlotItem, parentNode : this.props.parentNode });
		this.props.onBlur();
	}

	_renderMetadataTree() {
		const includeOptions = null;
		const context = this.props.slotDataItemHandler.getPickTreeContext && this.props.slotDataItemHandler.getPickTreeContext(this.props.replaceSlotItem);
		return this.props.dashboardApi.getFeature('DataSources.UI').getPickTree(this.props.sourceId , this.state.userTextInput, this.performSelection.bind(this), includeOptions, context);
	}
}

FieldDropdown.propTypes = {
	a11Y: PropTypes.object,
	open: PropTypes.bool.isRequired,
	textValue: PropTypes.string,
	dashboardApi: PropTypes.object.isRequired,
	getDropdownMaxHeight: PropTypes.func.isRequired,
	sourceId: PropTypes.string.isRequired,
	onBlur: PropTypes.func.isRequired,
	replaceSlotItem:PropTypes.bool.isRequired,
	parentNode: PropTypes.node,
	slotDataItemHandler: PropTypes.object.isRequired
};

FieldDropdown.defaultProps = {
	textValue: '',
	a11Y: undefined,
	onItemSelected: () => {}
};


export default FieldDropdown;



// WEBPACK FOOTER //
// ./src/js/FieldDropdown.js